import { useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import type { GameLink } from '@/features/events/defaultJourney';
import { useT } from '@/lib/i18n/useT';

/**
 * The link a guest scans, with the two things anyone ever wants to do with it:
 * copy it, or open it to check.
 *
 * Shared by the three-step card and the returning summary. It has to appear in
 * both, because creating an event already wires a station and a journey — so a
 * manager who follows the happy path finishes all three steps at once and the
 * checklist disappears before it can hand over the URL. Somewhere on the home,
 * at all times, "your game is live and here is the address" has to be legible.
 */
export function GameLinkPanel({ link }: { link: GameLink }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(link.url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="rounded-xl border border-[var(--color-accent-ink)] bg-[var(--color-bg-input)] p-3">
      <p className="mb-2 text-xs font-semibold text-[var(--color-accent-ink)]">
        {t('quickStart.linkReady')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code
          data-testid="game-link"
          className="min-w-0 flex-1 truncate rounded-lg bg-[var(--color-xk-bg-deep)] px-2 py-1.5 text-xs text-[var(--color-xk-text)]"
        >
          {link.url}
        </code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-2.5 py-1.5 text-xs font-semibold text-[var(--color-xk-text)] transition-colors hover:border-[var(--color-accent-ink)]"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? t('quickStart.copied') : t('quickStart.copy')}
        </button>
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-2.5 py-1.5 text-xs font-semibold text-[var(--color-fg-on-accent)] transition-opacity hover:opacity-90"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t('quickStart.preview')}
        </a>
      </div>
    </div>
  );
}
